import type { PaywallTheme } from '@/types';
import { APPEARANCE_ACCENT_TOKENS, themeVar } from '@/tokens';

// Applies a product's paywall theme to the shadow host.
//
// Template / mode → data-sm-template / data-sm-mode (styles.css -theme blocks).
// Accent → --sm-*-theme properties derived from one hex (Appearance accent).
//
// Token contract (styles.css + tokens.ts — single registry):
//
//   --sm-TOKEN: var(--sm-TOKEN-theme, <classic default>)
//
//   public --sm-TOKEN — publishers override on #ezoic-sm from page CSS.
//   -theme            — Appearance (here) and Modern/Dark in styles.css.
//   default           — classic/light when the theme layer is unset.

const APPEARANCE_THEME_VARS = APPEARANCE_ACCENT_TOKENS.map(themeVar);

const SIX_DIGIT_HEX = /^#[0-9a-f]{6}$/i;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb | null {
  if (!SIX_DIGIT_HEX.test(hex)) {
    return null;
  }
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) => Math.round(clamp(value, 0, 255)).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function darken(rgb: Rgb, amount: number): Rgb {
  const factor = 1 - amount;
  return { r: rgb.r * factor, g: rgb.g * factor, b: rgb.b * factor };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const linear = (channel: number) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

export function isDarkColor(color: string | undefined): boolean {
  if (!color) {
    return false;
  }
  const rgb = parseHex(color.trim());
  if (!rgb) {
    return false;
  }
  return relativeLuminance(rgb) < 0.5;
}

export function accentTokens(accentColor: string | undefined): Record<string, string> | null {
  if (!accentColor) {
    return null;
  }
  const rgb = parseHex(accentColor.trim());
  if (!rgb) {
    return null;
  }
  const onAccent = relativeLuminance(rgb) > 0.4 ? '#0f172a' : '#ffffff';
  return {
    [themeVar('accent')]: toHex(rgb),
    [themeVar('accent-hover')]: toHex(darken(rgb, 0.12)),
    [themeVar('focus')]: `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, 0.14)`,
    [themeVar('on-accent')]: onAccent,
  };
}

function resolveTemplate(theme: PaywallTheme | undefined): 'classic' | 'modern' {
  return theme?.template === 'modern' ? 'modern' : 'classic';
}

function resolveMode(theme: PaywallTheme | undefined): 'light' | 'dark' | 'auto' {
  if (theme?.mode === 'dark' || theme?.mode === 'auto') {
    return theme.mode;
  }
  return 'light';
}

function applyAccentTheme(host: HTMLElement, tokens: Record<string, string> | null): void {
  if (!tokens) {
    for (const name of APPEARANCE_THEME_VARS) {
      host.style.removeProperty(name);
    }
    return;
  }
  for (const [name, value] of Object.entries(tokens)) {
    host.style.setProperty(name, value);
  }
}

export function applyTheme(host: HTMLElement, theme?: PaywallTheme): void {
  const template = resolveTemplate(theme);
  if (template === 'classic') {
    host.removeAttribute('data-sm-template');
  } else {
    host.setAttribute('data-sm-template', template);
  }

  const mode = resolveMode(theme);
  if (mode === 'light') {
    host.removeAttribute('data-sm-mode');
  } else {
    host.setAttribute('data-sm-mode', mode);
  }

  applyAccentTheme(host, accentTokens(theme?.accentColor));
}
